
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

// Simple in-memory cache to prevent duplicate API calls
const cache = new Map();
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes - increased from 5 minutes

// Track in-flight requests to prevent duplicate calls
const inFlightRequests = new Map();

/**
 * Clear the cache
 */
export const clearProcedureCodesCache = () => {
  cache.clear();
  inFlightRequests.clear();
  console.log('Procedure codes cache cleared');
};

/**
 * Get cache statistics
 */
export const getCacheStats = () => {
  return {
    size: cache.size,
    keys: Array.from(cache.keys()),
    inFlight: inFlightRequests.size,
    inFlightKeys: Array.from(inFlightRequests.keys())
  };
};

/**
 * Debug function to log cache and request status
 */
export const debugProcedureCodesService = () => {
  console.log('=== Procedure Codes Service Debug ===');
  console.log('Cache size:', cache.size);
  console.log('Cache keys:', Array.from(cache.keys()));
  console.log('In-flight requests:', inFlightRequests.size);
  console.log('In-flight keys:', Array.from(inFlightRequests.keys()));
  console.log('=====================================');
};

/**
 * Clean expired cache entries
 */
const cleanExpiredCache = () => {
  const now = Date.now();
  for (const [key, value] of cache.entries()) {
    if (now - value.timestamp > CACHE_DURATION) {
      cache.delete(key);
    }
  }
};

// Clean expired cache entries every 5 minutes
setInterval(cleanExpiredCache, 5 * 60 * 1000);

/**
 * Secure Procedure Codes Service
 * Uses backend API endpoints instead of direct Firestore access
 */

/**
 * Get cached data if available and not expired
 */
const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

/**
 * Set data in cache with timestamp
 */
const setCachedData = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
};

/**
 * Get all procedure codes with optional filtering using cursor-based pagination
 * @param {Object} filters - Filters (type, search, active)
 * @param {number} limit - Items per page (max 1000)
 * @param {string} startAfter - Cursor for pagination (optional)
 * @returns {Promise<Object>} Procedure codes with cursor-based pagination
 */
export async function getAllProcedureCodes(filters = {}, limit = 500, startAfter = null) {
  try {
    // Create cache key based on filters
    const cacheKey = `procedureCodes_${JSON.stringify(filters)}_${limit}_${startAfter}`;
    
    // Check cache first
    const cached = getCachedData(cacheKey);
    if (cached) {
      console.log('Using cached procedure codes data');
      return cached;
    }

    console.log('Fetching procedure codes with filters:', filters);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    
    // Add startAfter cursor if provided
    if (startAfter) {
      params.append('startAfter', startAfter);
    }
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/referenceData/procedure-codes?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Backend response data:', response.data);
    
    const result = {
      procedureCodes: response.data.procedureCodes,
      pagination: response.data.pagination
    };
    
    // Cache the result
    setCachedData(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error('Error fetching procedure codes:', error);
    throw error;
  }
}

/**
 * Get procedure codes by type with optimized filtering
 * @param {string} procedureCodeType - Type of procedure codes
 * @param {Object} options - Additional options (activeOnly, search, limit)
 * @returns {Promise<Array>} Array of procedure codes
 */
export async function getProcedureCodesByType(procedureCodeType, options = {}) {
  try {
    console.log('Fetching procedure codes by type:', procedureCodeType);
    
    const filters = {
      type: procedureCodeType,
      active: options.activeOnly !== false ? 'true' : undefined, // Default to active only
      search: options.search
    };
    
    const limit = options.limit || 500;
    
    const result = await getAllProcedureCodes(filters, limit);
    return result.procedureCodes;
  } catch (error) {
    console.error('Error fetching procedure codes by type:', error);
    throw error;
  }
}

/**
 * Search procedure codes by type and search term
 * @param {string} procedureCodeType - Type of procedure codes
 * @param {string} searchTerm - Search term
 * @param {boolean} activeOnly - Whether to include only active codes
 * @returns {Promise<Array>} Array of matching procedure codes
 */
export async function searchProcedureCodesByType(procedureCodeType, searchTerm = '', activeOnly = true) {
  try {
    // Create cache key for search results
    const cacheKey = `search_${procedureCodeType}_${searchTerm}_${activeOnly}`;
    
    // Check cache first
    const cached = getCachedData(cacheKey);
    if (cached) {
      console.log('Using cached search results for:', searchTerm);
      return cached;
    }

    // Check if request is already in flight
    if (inFlightRequests.has(cacheKey)) {
      console.log('Request already in flight for:', cacheKey);
      return inFlightRequests.get(cacheKey);
    }

    console.log('Searching procedure codes:', procedureCodeType, searchTerm);
    
    const filters = {
      type: procedureCodeType,
      search: searchTerm,
      active: activeOnly ? 'true' : undefined
    };
    
    const limit = 500; // Reasonable limit for search results
    
    // Create the promise and store it in in-flight requests
    const searchPromise = getAllProcedureCodes(filters, limit).then(result => {
      // Cache the search results
      setCachedData(cacheKey, result.procedureCodes);
      // Remove from in-flight requests
      inFlightRequests.delete(cacheKey);
      return result.procedureCodes;
    }).catch(error => {
      // Remove from in-flight requests on error
      inFlightRequests.delete(cacheKey);
      throw error;
    });
    
    // Store the promise in in-flight requests
    inFlightRequests.set(cacheKey, searchPromise);
    
    return searchPromise;
  } catch (error) {
    console.error('Error searching procedure codes by type:', error);
    throw error;
  }
}

/**
 * Get a specific procedure code by ID
 * @param {string} codeId - Procedure code ID
 * @returns {Promise<Object>} Procedure code object
 */
export async function getProcedureCodeById(codeId) {
  try {
    console.log('Fetching procedure code by ID:', codeId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/referenceData/procedure-codes/${codeId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.procedureCode;
  } catch (error) {
    console.error('Error fetching procedure code by ID:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Create a new procedure code (admin only)
 * @param {Object} codeData - Procedure code data
 * @returns {Promise<Object>} Created procedure code
 */
export async function addProcedureCode(codeData) {
  try {
    console.log('Creating procedure code:', codeData.procedure_code);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/referenceData/procedure-codes`,
      codeData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error creating procedure code:', error);
    throw error;
  }
}

/**
 * Update an existing procedure code (admin only)
 * @param {string} codeId - Procedure code ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} Update result
 */
export async function updateProcedureCode(codeId, updateData) {
  try {
    console.log('Updating procedure code:', codeId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/referenceData/procedure-codes/${codeId}`,
      updateData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error updating procedure code:', error);
    throw error;
  }
}

/**
 * Deactivate a procedure code (admin only)
 * @param {string} codeId - Procedure code ID
 * @returns {Promise<Object>} Deactivation result
 */
export async function deactivateProcedureCode(codeId) {
  try {
    console.log('Deactivating procedure code:', codeId);
    const token = await getAuthToken();
    
    const response = await axios.delete(
      `${API_BASE}/referenceData/procedure-codes/${codeId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error deactivating procedure code:', error);
    throw error;
  }
}

/**
 * Bulk import procedure codes (admin only)
 * @param {Array} codes - Array of procedure code objects
 * @returns {Promise<Object>} Import result
 */
export async function bulkImportProcedureCodes(codes) {
  try {
    console.log('Bulk importing procedure codes:', codes.length, 'codes');
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/referenceData/procedure-codes/bulk-import`,
      { codes },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error bulk importing procedure codes:', error);
    throw error;
  }
}

/**
 * Get active procedure codes by type (optimized for dropdowns)
 * @param {string} procedureCodeType - Type of procedure codes
 * @returns {Promise<Array>} Array of active procedure codes
 */
export async function getActiveProcedureCodesByType(procedureCodeType) {
  try {
    return await getProcedureCodesByType(procedureCodeType, { 
      activeOnly: true,
      limit: 1000
    });
  } catch (error) {
    console.error('Error fetching active procedure codes by type:', error);
    throw error;
  }
}

/**
 * Search for procedure codes across all types
 * @param {string} searchTerm - Search term
 * @param {boolean} activeOnly - Whether to include only active codes
 * @param {number} limit - Maximum number of results
 * @returns {Promise<Array>} Array of matching procedure codes
 */
export async function searchAllProcedureCodes(searchTerm, activeOnly = true, limit = 100) {
  try {
    console.log('Searching all procedure codes for:', searchTerm);
    
    const filters = {
      search: searchTerm,
      active: activeOnly ? 'true' : undefined
    };
    
    const result = await getAllProcedureCodes(filters, limit);
    return result.procedureCodes;
  } catch (error) {
    console.error('Error searching all procedure codes:', error);
    throw error;
  }
}

/**
 * Get procedure code types available in the system
 * @returns {Promise<Array>} Array of unique procedure code types
 */
export async function getProcedureCodeTypes() {
  try {
    console.log('Fetching procedure code types');
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/referenceData/procedure-codes/types`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.types;
  } catch (error) {
    console.error('Error fetching procedure code types:', error);
    // Fallback to common types if endpoint doesn't exist
    return ['CPT', 'HCPCS', 'ICD-10-PCS', 'CDT'];
  }
}

/**
 * Validate a procedure code exists and is active
 * @param {string} procedureCode - Procedure code to validate
 * @param {string} codeType - Type of procedure code
 * @returns {Promise<boolean>} Whether the code is valid and active
 */
export async function validateProcedureCode(procedureCode, codeType) {
  try {
    const codes = await searchProcedureCodesByType(codeType, procedureCode, true);
    return codes.some(code => 
      code.procedure_code === procedureCode && code.active !== false
    );
  } catch (error) {
    console.error('Error validating procedure code:', error);
    return false;
  }
}

/**
 * Get procedure code statistics
 * @returns {Promise<Object>} Statistics about procedure codes
 */
export async function getProcedureCodeStatistics() {
  try {
    console.log('Fetching procedure code statistics');
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/referenceData/procedure-codes/statistics`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.statistics;
  } catch (error) {
    console.error('Error fetching procedure code statistics:', error);
    // Return empty statistics if endpoint doesn't exist
    return {
      totalCodes: 0,
      activeCount: 0,
      typeBreakdown: {}
    };
  }
}

/**
 * Get procedure codes with pagination (alias for getAllProcedureCodes)
 * @param {Object} filters - Filters (type, search, active)
 * @param {number} limit - Items per page (max 1000)
 * @param {string} startAfter - Cursor for pagination (optional)
 * @returns {Promise<Object>} Procedure codes with pagination
 */
export async function getProcedureCodesPaginated(filters = {}, limit = 500, startAfter = null) {
  console.log('getProcedureCodesPaginated called with:', { filters, limit, startAfter });
  
  // Map frontend parameter names to backend parameter names
  const mappedFilters = {};
  
  if (filters.procedureCodeType) {
    mappedFilters.type = filters.procedureCodeType;
  }
  if (filters.activeOnly !== undefined) {
    mappedFilters.active = filters.activeOnly ? 'true' : 'false';
  }
  if (filters.search) {
    mappedFilters.search = filters.search;
  }
  if (filters.pageSize) {
    limit = filters.pageSize;
  }
  
  console.log('Mapped filters:', mappedFilters);
  
  const result = await getAllProcedureCodes(mappedFilters, limit, startAfter);
  console.log('getAllProcedureCodes result:', result);
  
  return result;
}